import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Popover,
  RadioGroup,
  makeStyles,
  Theme
} from '@material-ui/core';
import { Button } from '../../atomicComponents';
import Radio from '@material-ui/core/Radio';
import { withAllowedOperationsProvider } from '../../hocs';
import {
  DATE_FORMAT,
  DISPLAY_MESSAGES,
  RESOURCE_TYPE,
  ASN_STATUS,
  AGGREGATE_STATUS,
} from '../../constants';
import clsx from 'clsx';
import styles from './AssignShiftForm.module.css';
import moment from 'moment';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { buildErrorMessage } from '../../apis/calls';

const AssignShiftForm = ({
  open,
  anchorEl,
  closeForm,
  activeAsnDate,
  shifts,
  openBulkAssignShiftForm,
  asns,
  reloadData,
  isLoading,
  assignShift,
  classNames,
  title,
}) => {
  const {
    showError,
    showSuccess,
    showInformation,
    showInternalError,
    showLoading,
    closePopUp,
  } = usePopupManager();
  const [selectedShiftValue, setSelectedShiftValue] = useState(null);

  const isAnyShiftActive = shifts.length > 0;

  const deriveFormSubTitle = date => {
    if (!date) {
      return `No ${title}s available`;
    }

    return date?.isBefore(moment().format(DATE_FORMAT.ISO))
      ? `Prev. ${title}s`
      : date?.format(DATE_FORMAT.DD_MM_YYYY);
  };

  const handleShiftChange = event => {
    setSelectedShiftValue(Number(event.target.value));
  };

  const handleBulkAssignShift = () => {
    closeForm();
    openBulkAssignShiftForm();
  };

  const areSelectedAsnsValid = selectedAsns => {
    if (selectedAsns.length === 0) {
      showInformation({
        contextText: `No ${title}s selected for shift assignment.`,
        info: '',
      });
      return false;
    }
    const invalidAsns = selectedAsns.find(
      asn =>
        asn.status !== ASN_STATUS.PLANNED &&
        asn.status !== AGGREGATE_STATUS.ASN_GENERATED,
    );

    if (invalidAsns) {
      showError({
        contextText: 'Shift assignment',
        info: `Shift can be assigned only for PENDING ${title}s`,
      });
      return false;
    }
    return true;
  };

  const handleApply = async () => {
    if (areSelectedAsnsValid(asns)) {
      showLoading();
      try {
        await assignShift(asns, selectedShiftValue);
        closeForm();
        reloadData();
        closePopUp();
        showSuccess({
          contextText: DISPLAY_MESSAGES.SHIFT_ASSIGNMENT_SUCCESS,
        });
      } catch (error) {
        console.error(buildErrorMessage(error));
        showInternalError();
      }
    }
  };

  const closeAssignShiftForm = () => {
    setSelectedShiftValue(deriveFirstAvailableShiftValue);
    closeForm();
  };

  const renderShiftOrAsnUnavailabilityMessage = () => {
    if (!activeAsnDate) {
      return (
        <>
          <Button
            className={clsx(styles.button, styles.closeButton)}
            onClick={closeForm}
          >
            Close
          </Button>
        </>
      );
    }
    return (
      <>
        <div className={styles.notAvailable}>
          Shift is not defined for the following date in IPMS 4.0
        </div>
        <Button
          className={clsx(styles.button, styles.applyOrCloseButton)}
          onClick={closeForm}
        >
          Close
        </Button>
      </>
    );
  };
  const deriveFirstAvailableShiftValue = () => {
    const firstAvailableShift = shifts[0];
    let shiftValue = null;
    if (firstAvailableShift) {
      shiftValue = firstAvailableShift.value;
    }
    return shiftValue;
  };

  useEffect(() => {
    setSelectedShiftValue(deriveFirstAvailableShiftValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shifts]);


  const useStyles = makeStyles((Theme) => ({
    backDrop: {
      backdropFilter: "blur(3px)",
      backgroundColor: 'rgba(0,0,30,0.4)'
    },
  }));
  const classes = useStyles();
  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closeAssignShiftForm}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        className={clsx(styles.paper, classNames.assignShiftFormPaper)}
        BackdropProps={{
          classes: {
            root: classes.backDrop,
          },
        }}
      >
        {isLoading && (
          <div className={clsx(styles.loaderIcon, styles.formBox)}>
            <CircularProgress />
          </div>
        )}
        {!isLoading && (
          <FormControl className={styles.formBox}>
            <div className={styles.formTitleNew}>
              <span className={styles.formTitle}>Select Shift</span>
              <div
                className={styles.formlLineDiv}
              />
            </div>
            <Box bgcolor="#dadada3d" paddingTop="4px" paddingBottom="4px" borderRadius='6px'>
              <span className={styles.dateSpan}>Date</span>
              <span style={{ display: "inline", marginLeft: "22px" }} className={styles.formSubTitle}>
                {deriveFormSubTitle(activeAsnDate)}
              </span>
            </Box>
            <div className={styles.partitionLine}>

            </div>
            {isAnyShiftActive && (
              <>

                <Box bgcolor="#dadada3d" paddingTop="10px" paddingBottom="1px" borderRadius='6px'>
                  <RadioGroup
                    aria-label="shift"
                    name="shift"
                    value={selectedShiftValue}
                    onChange={handleShiftChange}
                  >
                    {shifts.map(shift => (
                      <FormControlLabel
                        value={shift.value}
                        control={<Radio color="default" />}
                        label={shift.label === 'G' ? 'General(G)' : shift.label}
                        className={styles.radioLabel}
                      />
                    ))}
                  </RadioGroup>
                </Box>

                {title !== 'ASN' && (
                  <Button
                    className={clsx(styles.button, styles.bulkAssignmentButton)}
                    onClick={handleBulkAssignShift}
                  >
                    Bulk Assignment
                  </Button>
                )}
                <div style={{ float: 'right' }}>
                  <Button
                    variant='primary'
                    className={clsx(styles.button, styles.applyOrCloseButton)}
                    onClick={handleApply}
                  >
                    APPLY
                  </Button>
                </div>
              </>
            )}
            {!isAnyShiftActive && renderShiftOrAsnUnavailabilityMessage()}
          </FormControl>
        )}
      </Popover>
    </>
  );
};

AssignShiftForm.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.any.isRequired,
  closeForm: PropTypes.func.isRequired,
  activeAsnDate: PropTypes.any,
  shifts: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      isAvailable: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  openBulkAssignShiftForm: PropTypes.func.isRequired,
  asns: PropTypes.arrayOf(
    PropTypes.shape({
      asnId: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      isChecked: PropTypes.bool.isRequired,
    }),
  ),
  reloadData: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  assignShift: PropTypes.func.isRequired,
  classNames: PropTypes.object,
};

export default withAllowedOperationsProvider(
  AssignShiftForm,
  false,
  RESOURCE_TYPE.CHASSIS,
);
